import * as React from "react";
import { Button, Flex, Heading, SelectField, Grid } from "@aws-amplify/ui-react";
import { Container, Card } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { Friends } from "../models";
import { DataStore, Auth } from "aws-amplify";
import ListComponent from "./ListComponent";
import { createActiveGame } from "../lib/activeGameService";

export default function GolfGame() {
  const navigate = useNavigate();
  const [currentSelectedPlayer, setCurrentSelectedPlayer] = React.useState("");
  const [friendMap, setFriendMap] = React.useState(new Map());
  const [playerList, setPlayerList] = React.useState([]);
  const [friendList, setFriendList] = React.useState("");
  const [initialDealer, setInitialDealer] = React.useState("");

  React.useEffect(() => {
    const loadFriends = async () => {
      const owner = await getOwner();
      const friends = await DataStore.query(Friends, f => f.FriendOf.eq(owner));
      setFriendList(friends?.map((item) => <option value={item.id} key={item.id}>{item.Name}</option>));
      const newFriendMap = new Map();
      friends?.forEach((item) => {
        newFriendMap.set(item.id, item.Name);
      });
      setFriendMap(newFriendMap);
      if (friends && friends.length > 0) {
        setCurrentSelectedPlayer(friends[0]?.id);
      }
    };
    loadFriends();
  }, []);

  async function getOwner() {
    let user = await Auth.currentAuthenticatedUser();
    return user.username;
  }

  const handleAddPlayer = (event) => {
    event.preventDefault();
    const playerName = friendMap.get(currentSelectedPlayer);
    if (playerName) {
      const newList = [...playerList];
      const isFirstPlayer = newList.length === 0; // Check if this is the first player
      
      newList.push(
        <li key={currentSelectedPlayer} value={playerName}>
          {playerName}
        </li>
      );
      setPlayerList(newList);
      
      // Auto-select first player as initial dealer
      if (isFirstPlayer) {
        setInitialDealer(playerName);
      }
      
      // Remove this player from the available options
      const newFriendMap = new Map(friendMap);
      newFriendMap.delete(currentSelectedPlayer);
      setFriendMap(newFriendMap);
      
      // Update the select field options
      const updatedFriendList = Array.from(newFriendMap.entries()).map(([id, name]) => (
        <option value={id} key={id}>{name}</option>
      ));
      setFriendList(updatedFriendList);
      
      // Set current selection to first remaining player
      if (newFriendMap.size > 0) {
        const firstKey = newFriendMap.keys().next().value;
        setCurrentSelectedPlayer(firstKey);
      } else {
        setCurrentSelectedPlayer("");
      }
    }
  };

  const handleStartGame = (event) => {
    event.preventDefault();
    if (playerList.length < 2) {
      alert("Please add at least 2 players to start a game");
      return;
    }

    if (!initialDealer) {
      alert("Please select an initial dealer before starting the game");
      return;
    }

    // Create player objects for Golf game
    const golfPlayers = playerList.map((item, index) => ({
      id: index,
      name: item.props.value,
      scores: [], // Array of round scores
      total: 0    // Total cumulative score
    }));

    try {
      const now = new Date();
      const displayName = `Golf - ${now.toLocaleString()}`;
      const id = createActiveGame({
        gameType: 'GOLF',
        displayName,
        players: golfPlayers,
        snapshot: { players: golfPlayers, currentRound: 1, completedRounds: [] },
        route: '/golf-scorecard'
      });
      navigate('/golf-scorecard', {
        state: {
          players: golfPlayers,
          initialDealer: initialDealer,
          activeGameId: id
        }
      });
    } catch (e) {
      navigate('/golf-scorecard', { state: { players: golfPlayers, initialDealer: initialDealer } });
    }
  };

  return (
    <Container>
      <Flex direction="column" gap="20px" padding="20px">
        <Heading level={2}>Golf Card Game Calculator</Heading>

        <Card>
          <Card.Body>
            <Card.Title>Select Players</Card.Title>
            <Card.Text>
              Choose from your friends to play Golf. You need at least 2 players to start.
            </Card.Text>
          </Card.Body>
        </Card>

        <Grid rowGap="15px" columnGap="15px">
          <SelectField
            label="Players"
            descriptiveText="Select a player, then press 'Add Player'"
            onChange={(e) => {
              setCurrentSelectedPlayer(e.target.value);
            }}
            value={currentSelectedPlayer}
          >
            {friendList}
          </SelectField>

          <Flex justifyContent="space-between">
            <Button
              children="Add Player"
              onClick={handleAddPlayer}
              isDisabled={!currentSelectedPlayer || friendMap.size === 0}
            />
          </Flex>

          <div>
            <h5>Players in this game:</h5>
            <ListComponent items={playerList} />
          </div>

          {playerList.length > 0 && (
            <SelectField
              label="Initial Dealer"
              descriptiveText="Select who should be the first dealer"
              onChange={(e) => {
                setInitialDealer(e.target.value);
              }}
              value={initialDealer}
            >
              {playerList.map((item) => (
                <option value={item.props.value} key={item.key}>
                  {item.props.value}
                </option>
              ))}
            </SelectField>
          )}
        </Grid>

        <Flex justifyContent="center" gap="15px" paddingTop="20px">
          <Button
            children="Start Game"
            className="start-game-button"
            onClick={handleStartGame}
            variation="primary"
            isDisabled={playerList.length < 2 || !initialDealer}
          />
          <Button
            onClick={() => navigate("/newgame")}
            variation="secondary"
          >
            Cancel
          </Button>
        </Flex>
      </Flex>
    </Container>
  );
}
